import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { ClaimsService } from '@max/auth/services/claims.service';
import { CustomerSearchResultModel } from '@max/customer/search/models/customer-search-result.model';
import { BusinessTransactionType } from '@max/foundation/models/enums/business-transaction-type.enum';
import { BaseComponent } from '@max/shared/base-components/base.component';
import { ValidationModel } from '@max/shared/models/validation-results/validation.model';
import { MvdModalService } from '@max/shared/services/modal/mvd-modal.service';
import { BusinessTransactionItemKey } from '../models/business-transaction-itemkey';
import { BusinessTransactionValidationRequest } from '../models/business-transaction-validation-request';
import { NewBusinessTransaction } from '../models/new-business-transaction.model';
import { DifferentAgentCartWindow } from '../queue/modals/different-agent-cart-modal.component';
import { CheckinService } from '../services/checkin.service';
import * as moment from 'moment';
import { ValidationResultModel } from '../../shared/models/validation-results/validation-result.model';
import { ValidationType } from '../../Foundation/models/enums/validation-type.enum';
import { ValidationStateModel } from '../../shared/models/validation-results/validation-state.model';
import { VehicleService } from '../../vehicle/services/vehicle.service';

@Component({
    selector: 'transaction-prequal',
    templateUrl: './transaction-prequal.html'
})
export class TransactionPrequalComponent extends BaseComponent implements OnChanges {
    @Input() agentId: any;
    @Input() customerNumberId: number;
    @Input() serviceRequirementId: number;
    @Input() aircraftId: number;
    @Input() transactionValidationMode: number;
    @Input() validation: any;
    @Input() transactionTypeId: any;
    @Input() businessTransactionParamId: number;
    @Input() businessTransactionParamTypeId: number;
    @Output() changed: EventEmitter<Object> = new EventEmitter();
    @Output() cancel: EventEmitter<void> = new EventEmitter<void>();
    @Output() customerChanged: EventEmitter<any> = new EventEmitter();

    public checkinMode = false;
    public containsDriverTrans = false;
    public currentlySelectedCustomer: CustomerSearchResultModel;
    private currentlySelectedService: any;
    public displayRequiredControl = false;
    private fetchingRequiredComponents = false;
    public filteredServices: any[];
    public loadServiceAutoComplete = false;

    /**
     * The model sent for business transaction validations
     * Note: The properties on this model for vehicle and customer are get/set properties.
     * On set the item key will set/overwrite the specified item key in a collection vs setting
     * a local property on the model.
     * */
    public model: BusinessTransactionValidationRequest = new BusinessTransactionValidationRequest();

    public navigateToTransaction = false;
    public photoFirstPresent = false;
    public readOnly = false;
    public service: any;
    public serviceDetails: any;
    public serviceReadOnly = false;
    public showProceed = false;
    public showTransactionTypeComponent = false;
    public showVehicleSearchComponent = false;
    public showCommercialPermitSearchComponent = false;
    public showIRPSearchComponent = false;
    public showAircraftSearchComponent = false;
    public startingTransaction = false;
    public stopForValidationErrors: boolean;
    private currentlySelectedVehicle: any;
    private driverTransactionIds: number[] = [38, 61, 62, 65, 70, 1066, 1067, 1068, 1069, 1070, 1071];
    private servicesList: any[] = [];
    private servicesLoaded = false;
    private transactionTypes = new BusinessTransactionType();

    private informationalMessagesConfirmed = false;

    constructor(
        public modalService: MvdModalService,
        private checkinService: CheckinService,
        private changeDetectorRef: ChangeDetectorRef,
        private claimsService: ClaimsService,
        private vehicleService: VehicleService
    ) {
        super();
    }

    public ngOnChanges() {
        if (this.validation) {
            this.handleValidationResult(this.validation);
            this.startingTransaction = false;
        }
    }

    public ngOnInit() {
        if (this.validation) {
            this.handleValidationResult(this.validation);
            if (this.validation.error) {
                this.evaluateErrorForArea(this.validation.error);
            }

            // show "proceed to transaction" button only if validation is valid and not clean.
            if (!this.validation.isClean && this.validation.isValid) {
                this.navigateToTransaction = true;
                this.changeDetectorRef.detectChanges();
            }
        }

        if (this.isInGreeter()) {
            this.checkinMode = true;
        }

        if (!!this.serviceRequirementId) {
            this.displayRequiredControl = true;
            this.showVehicleSearchComponent = true;
            const vehicle = {
                vehicleId: this.serviceRequirementId
            };
            this.vehicleSelected(vehicle);
        }

        if (!!this.aircraftId) {
            this.displayRequiredControl = true;
            this.showAircraftSearchComponent = true;

            const businessTransactionObject = { businessTransactionParamId: this.aircraftId, businessTransactionItemKeyTypeId: 27 };
            this.onBusinessTransactionParamSelected(businessTransactionObject);
        }

        if (this.businessTransactionParamId && this.businessTransactionParamTypeId) {
            let genericItemKey = new BusinessTransactionItemKey();
            genericItemKey.businessTransactionItemKeyTypeId = this.businessTransactionParamTypeId;
            genericItemKey.itemKeyId = this.businessTransactionParamId;
            this.model.itemKeys.push(genericItemKey);
        }

        this.checkinService.getServicesList().subscribe(services => {
            const businessTransactionSourceSystemTypes = services.result.businessTransactionSourceSystemTypes ?
                services.result.businessTransactionSourceSystemTypes : [];
            this.loadServiceAutoComplete = true;

            if (this.isInModal()) {
                this.serviceReadOnly = true;

                if (this.transactionTypeId) {
                    const id = this.transactionTypeId;

                    let result = businessTransactionSourceSystemTypes.filter(obj => {
                        if (obj.value == id) {
                            return obj;
                        }
                    });

                    this.service = result[0];
                    this.serviceChanged(result[0]);

                    if (this.customerNumberId) {
                        this.readOnly = true;
                    }
                }
            }

            this.getSecurityClaims(businessTransactionSourceSystemTypes);
        });

    }

    private intersectServiceClaims = (servicesList: any[], claimsList: any[]): any[] => this.operation(servicesList, claimsList, true);

    private operation = (list1: any[], list2: any[], operationIsUnion: boolean): any[] => {
        const result = [];

        for (let i = 0; i < list1.length; i++) {
            const item1 = list1[i];
            let found = false;

            for (let j = 0; j < list2.length; j++) {
                if (item1.businessTransactionValue === list2[j].value) {
                    found = true;
                    break;
                }
            }

            if (found === operationIsUnion) {
                result.push(item1);
            }
        }
        return result;
    };

    public customerSelected = (event: CustomerSearchResultModel): void => {
        this.model.customerKey = new BusinessTransactionItemKey(event && event.customerNumber, 1);
        this.currentlySelectedCustomer = event;
        this.changeDetectorRef.detectChanges();
        this.checkinService.getRecentDriverPhotoExists(this.currentlySelectedCustomer.customerNumber)
            .subscribe(t => this.photoFirstPresent = t.result);
        this.customerChanged.emit(this.currentlySelectedCustomer.customerNumber);
    };

    public serviceChanged = (event): void => {
        const id: number = +event.value;
        this.model.businessTransactionTypeId = id;
        this.model.businessTransactionAreaId = event.sourceSystemId;
        this.currentlySelectedService = event;
        this.getRequiredSearchCriteria(id);
        if (this.driverTransactionIds.includes(id) == true) {
            if (!this.containsDriverTrans) {
                this.containsDriverTrans = true;
            }
        }

        if (!this.serviceRequirementId && !this.aircraftId && event.sourceSystemId !== 11) {
            this.clearVehicleSelection();
        }

        this.promptIsVehicleBeingRemovedFromApportionedStatus();
    };

    private getRequiredSearchCriteria = (id: number): void => {
        this.fetchingRequiredComponents = true;
        this.checkinService
            .getRequiredSearchCriteria(id)
            .subscribe(response => {
                this.displayRequiredControl = false;
                this.showVehicleSearchComponent = false;
                this.showTransactionTypeComponent = false;

                if (response && response.result && response.result.length) {
                    this.displayRequiredControl = true;
                    for (let i = 0; i < response.result.length; i++) {
                        if (response.result[i] === 1) {
                            this.showVehicleSearchComponent = true;
                        } else if (response.result[i] === 2) {
                            this.showTransactionTypeComponent = true;
                        } else if (response.result[i] === 3) {
                            this.showCommercialPermitSearchComponent = true;
                        } else if (response.result[i] === 4) {
                            this.showIRPSearchComponent = true;
                        } else if (response.result[i] === 5) {
                            this.showAircraftSearchComponent = true;
                        }
                    }
                }
                this.fetchingRequiredComponents = false;
            });
    };

    public vehicleSelected = (vehicle): void => {
        this.model.vehicleKey = new BusinessTransactionItemKey(vehicle && vehicle.vehicleId, 2);
        this.currentlySelectedVehicle = vehicle;

        this.promptIsVehicleBeingRemovedFromApportionedStatus();
    };

    public onAircraftSelected = (aircraft): void => {
        this.model.vehicleKey = new BusinessTransactionItemKey(aircraft.aircraftId, 27);
        this.stopForValidationErrors = false;
    };

    /**
     * Set the business transaction param and businessTransactionItemKeyType when the value is selected from txn-type component
     * @param businessTransactionObject
     */
    public onBusinessTransactionParamSelected = (businessTransactionObject: any): void => {
        this.model.vehicleKey = new BusinessTransactionItemKey(businessTransactionObject.businessTransactionParamId, businessTransactionObject.businessTransactionItemKeyTypeId);
        this.stopForValidationErrors = false;
    };

    // setting the itemkeys to undefined so that the 400 gets thrown properly
    public onBusinessTransactionParamRemoved = (): void => {
        this.model.vehicleKey = new BusinessTransactionItemKey();
    };

    /**
     * Sets the business transaction params
     * */
    private SetBusinessTransactionParams(outputModel: NewBusinessTransaction) {
        if (this.businessTransactionParamId && this.businessTransactionParamTypeId) {
            outputModel.businessTransactionParamId = this.businessTransactionParamId;
            outputModel.businessTransactionParamTypeId = this.businessTransactionParamTypeId;
        } else {
            outputModel.businessTransactionParamId = this.model.vehicleKey.itemKeyId;
            outputModel.businessTransactionParamTypeId = this.model.vehicleKey.businessTransactionItemKeyTypeId;
        }

        if (this.showIRPSearchComponent) {
            outputModel.businessTransactionParam2Id = this.model.vehicleKey.itemKeyId;
            outputModel.businessTransactionParamType2Id = this.model.vehicleKey.businessTransactionItemKeyTypeId;
        } else if (this.showAircraftSearchComponent) {
            // not being consumed at this time
        } else if (this.showCommercialPermitSearchComponent) {
            // not being consumed at this time
        }
    }

    // constructs the data needed for adding it to side bar
    public emitGreeterModel = (data): void => {
        this.startingTransaction = true;
        const outputModel: NewBusinessTransaction = new NewBusinessTransaction();
        outputModel.agentId = parseInt(this.agentId);
        outputModel.businessTransactionAreaId = this.model.businessTransactionAreaId; // This identifies Drivers, Finance, T&R.  It is NOT businessTransactionTypeId

        // If there is not duplicate transaction
        if (data.businessTransactionId == null || data.businessTransactionId == 0) {
            outputModel.businessTransactionNumberId = this.model.businessTransactionId;
            outputModel.wipStatusTypeValue = 'New';
        } else {
            // If the serivice is already existing with the details.
            outputModel.businessTransactionNumberId = data.businessTransactionId;
            if (data.details) {
                outputModel.businessUnitName = data.details.businessUnitName;
                outputModel.wipStatusTypeValue = data.details.wipStatusTypeId.toString();
            }
        }

        outputModel.businessTransactionTypeId = this.model.businessTransactionTypeId;
        outputModel.businessTransactionTypeValue = this.currentlySelectedService && this.currentlySelectedService['text'];
        outputModel.businessUnitId = this.auth.businessUnitId;

        this.SetBusinessTransactionParams(outputModel);

        if (this.currentlySelectedCustomer) {
            outputModel.customerNumberId = this.currentlySelectedCustomer.customerNumber;
            outputModel.firstName = this.currentlySelectedCustomer.customerName;
            outputModel.name = this.currentlySelectedCustomer.customerName;
        }

        if (this.currentlySelectedVehicle) {
            outputModel.details = this.currentlySelectedVehicle['modelYear'] + ' '
                + this.currentlySelectedVehicle['makeDisplayValue'] + ' '
                + this.currentlySelectedVehicle['modelDisplayValue'];
        }

        outputModel.disabled = true;
        outputModel.transactionValidationMode = this.model.businesstransactionValidationMode;
        this.changed.emit(outputModel);
    };

    /**
     * Handles adding a new service either from the transaction prequal modal or when rehydrating a transaction
     * This method will call several sub methods to handle validating the service,
     * checking duplicate services, checking for different agents, etc
     * NOTE: This method will ultimatly cause a different model to be returned compared to the greeter mode
     * */
    public addNewService = (): void => {
        this.startingTransaction = true;
        this.model.businesstransactionValidationMode = this.transactionValidationMode;
        this.validateForCheckin(this.model);
    };

    private checkDifferentAgent = (result: any): void => {

        // 1. If we ARE in the proceed to service mode (ie !checkinMode and navigateToTransaction) then start the service
        // 2. If we are NOT in the proceed to service mode (ie !checkinMode and navigateToTransaction)
        // 3. If we have informational messages and they have not confirmed then make them confirm
        // 4. They have confirmed so start the service

        if (!this.checkinMode && this.navigateToTransaction) {
            this.checkDifferentAgentForCurrentCart(result);
        } else if (this.validationResults.getInformationalMessages(undefined).length > 0 && this.informationalMessagesConfirmed == false) {
            this.informationalMessagesConfirmed = true;
            this.handleValidationResult(result.validationResults);
            this.startingTransaction = false;
        } else {
            this.checkDifferentAgentForCurrentCart(result);
        }
    };

    /**
     * Show wip restore alert message
     * @param result
     */
    private showWipRestoreAlert = (result: any): void => {
        if (result.details != null) {
            const message = 'This service is restored from wip, Last updated date on ' + moment(result.details.updatedDate).format('MM/DD/YYYY') + '.';
            this.modalService.alert().title('Restore from WIP')
                .body(message)
                .open()
                .afterClosed()
                .subscribe(() => this.checkDifferentAgent(result));
        }
    };

    private validateForCheckin = (service: BusinessTransactionValidationRequest): void => {
        // Checks if the Dupicate service already exists, If yes, the payload will return the necessary service details to resume the transaction.
        this.checkinService.getServiceCheckinDetails(service)
            .subscribe(res => {
                this.checkinService.validateServices(res)
                    .subscribe(result => {

                        // If for some reason no object come back then just continue
                        if (result.validationResults == null) {
                            this.checkDifferentAgentForCurrentCart(result);
                        } else {
                            this.validationResults = new ValidationModel(result.validationResults);

                            // If there are any errors then handle them and do not start the service
                            if (this.validationResults.getErrors(undefined).length > 0) {
                                this.handleValidationResult(result.validationResults);
                                this.startingTransaction = false;
                            } else {
                                if (result.businessTransactionId != null && result.businessTransactionId > 0) {
                                    this.showWipRestoreAlert(result);
                                } else {
                                    this.checkDifferentAgent(result);
                                }
                            }
                        }
                    }, e => {
                        this.handleValidationResult(e);

                        if (this.isInGreeter() && !(this.disallowedTransactions(e))) {
                            this.showProceed = true;
                            this.serviceDetails = res;
                        }

                        this.navigateToTransaction = false;
                        const response = e.error;

                        if (null != response.validationResults) {
                            this.stopForValidationErrors = response.validationResults.some(i => 'Error' == i.state.validationType);
                        }

                        this.startingTransaction = false;
                    }, () => {
                    });
            });
    };

    private checkDifferentAgentForCurrentCart = (data): void => {
        if (data.businessTransactionValidationActionTypes && data.businessTransactionValidationActionTypes.indexOf(1) >= 0) {
            // TODO: Create businessTransactionValidationActionTypes enum
            this.modalService.open(DifferentAgentCartWindow).afterClosed().subscribe(() => {
                this.checkForDuplicateService(data);
            });
        } else {
            this.checkForDuplicateService(data);
        }
    };

    private checkForDuplicateService = (data): void => {
        this.handleValidationResult(data);

        /** The model that will be returned emitted
         * is not the same model that wil be returned in other cases such as the greeter.
         * It will return the model returned by the Validation call.
        **/
        if (this.isInModal()) {
            this.changed.emit(data);
        }

        /** The model that will be returned emitted by the emitGreeterModel method
         * is not the same model that wil be returned in other cases such as if we are in a modal.
        **/
        if (this.isInGreeter()) {
            this.emitGreeterModel(data);
            this.startingTransaction = false;
        }
    };

    private isInModal() {
        return this.transactionValidationMode === 2;
    }

    private isInGreeter() {
        return this.transactionValidationMode === 1;
    }

    private disallowedTransactions(e: any) {
        const transactionType: BusinessTransactionType = new BusinessTransactionType();
        const isOrganizationCustomer = this.currentlySelectedCustomer.customerTypeId === 2;
        const isIssueTemporaryPlacardTransaction = e.error.businessTransactionTypeId === transactionType.IssueTemporaryPlacard.id;

        // if stopTransactionCreation flag is set in in the state object, we do not want the transaction to continue
        var stopTransactionCreation = false;
        if (e.error.validationResults) {
            for (var i = 0; i < e.error.validationResults.length; i++) {
                let validationResult = e.error.validationResults[i];
                if (validationResult.state && validationResult.state.stopTransactionCreation) {
                    stopTransactionCreation = true;
                }
            }
        }

        return (isOrganizationCustomer && isIssueTemporaryPlacardTransaction) || stopTransactionCreation;
    }

    public enableCustomerField = (): boolean => this.servicesLoaded;

    public filterServices = (event): void => {
        const searchFragment = event.query;
        this.filteredServices = this.filterServiceHelper(searchFragment);
    };

    private filterServiceHelper = (searchFragment): any[] => {
        const filteredList: any[] = [];

        this.servicesList.forEach(service => {
            if (service.text.toLowerCase().indexOf(searchFragment.toLowerCase()) > -1) {
                filteredList.push(service);
            }
        });

        return filteredList;
    };

    private getSecurityClaims(businessTransactionSourceSystemTypes: any) {
        this.claimsService.getSecurityClaims().subscribe(claims => {
            // List of all businessTypes available in the system
            const allServices = businessTransactionSourceSystemTypes;
            // Security transactions available for user/BU
            const claimsList = claims.result.securityClaims;
            // Gets the intersection of above two, to be populates in services lookup.
            this.servicesList = this.intersectServiceClaims(allServices, claimsList);
            this.servicesLoaded = true;
        });
    }

    public requiredFieldsNotFilled(): boolean {
        return !this.currentlySelectedService ||
            !this.currentlySelectedCustomer ||
            ((this.showVehicleSearchComponent || this.showTransactionTypeComponent || this.showAircraftSearchComponent) && !this.model.vehicleKey.itemKeyId) ||
            this.fetchingRequiredComponents;
    }

    public cancelPrequal = (): void => {
        this.cancel.emit(null);
    };

    public vehicleRemoved() {
        this.clearVehicleSelection();
    }

    public onAircraftRemoved() {
        this.model.vehicleKey = new BusinessTransactionItemKey();
    }

    private clearVehicleSelection() {
        this.informationalMessagesConfirmed = false;
        this.model.vehicleKey = new BusinessTransactionItemKey();
        this.currentlySelectedVehicle = null;
        this.stopForValidationErrors = false;
    }

    public customerRemoved() {
        this.informationalMessagesConfirmed = false;
        this.currentlySelectedCustomer = null;
        this.stopForValidationErrors = false;
    }

    private evaluateErrorForArea(error: any) {
        if (error.businessTransactionAreaId === 11) {
            let fleetKey = error.itemKeys.find(k => k.businessTransactionItemKeyTypeId === 31);
            let accountKey = error.itemKeys.find(k => k.businessTransactionItemKeyTypeId === 30);
            if (fleetKey) {
                this.businessTransactionParamId = fleetKey.itemKeyId;
                this.businessTransactionParamTypeId = 31;
            } else if (accountKey) {
                this.businessTransactionParamId = accountKey.itemKeyId;
                this.businessTransactionParamTypeId = 30;
            }

            this.stopForValidationErrors = true;
        }
    }

    public handleDisplayError(error: string) {
        var errorResult = this.createValidationResult(error, new ValidationType().Error.value);

        if (this.validationResults == null) {
            this.validationResults = new ValidationModel([]);
        }

        this.validationResults.validationResults.push(errorResult);
        this.handleValidationResult(this.validationResults);
    }

    /**
     * Handles displaying an information message on the prequal
     * @param message
     */
    public handleDisplayInformationalMessage(message: any) {
        var infoValidationResult = this.createValidationResult(message, new ValidationType().Information.value);

        if (this.validationResults == null) {
            this.validationResults = new ValidationModel([]);
        }

        this.validationResults.validationResults.push(infoValidationResult);
        this.handleValidationResult(this.validationResults);
    }

    /**
     * Creates a new validation model with specified error message
     * @param message error message
     * @returns validation model
     */
    private createValidationResult(message: string, validationType: string) {
        const val: ValidationResultModel = new ValidationResultModel();
        const state: ValidationStateModel = new ValidationStateModel();
        state.validationType = validationType;
        val.errorMessage = message;
        val.source = 'Model';
        val.state = state;
        return val;
    }

    private async promptIsVehicleBeingRemovedFromApportionedStatus() {
        if (this.currentlySelectedVehicle && this.currentlySelectedService) {
            let businessTransactionTypeIds = [this.transactionTypes.TitleOnly.id, this.transactionTypes.RegistrationOnly.id, this.transactionTypes.TitleandRegistration.id];
            if (this.currentlySelectedVehicle.isApportioned === undefined) {
                let vehicleComposite = await this.vehicleService.getVehicleComposite(this.currentlySelectedVehicle.vehicleId).toPromise();
                this.currentlySelectedVehicle.isApportioned = vehicleComposite.isApportioned;
            }
            if (businessTransactionTypeIds.includes(+this.currentlySelectedService.value) && this.currentlySelectedVehicle.isApportioned === true) {
                let response = await this.modalService.confirm().title('Alert').body('Is this vehicle being transferred to a NON IRP/Apportioned registration, or are you adding a title to an existing IRP Registration Only?')
                    .okBtn('Yes').cancelBtn('No').disableXButton()
                    .open().afterClosed().toPromise();

                if (!response) {
                    let validationResult = this.createValidationResult('Unable to process, please contact the Motor Carrier business unit', new ValidationType().Error.value);
                    if (this.validationResults == null) {
                        this.validationResults = new ValidationModel([]);
                    }

                    this.validationResults.validationResults.push(validationResult);
                    this.handleValidationResult(this.validationResults);
                    this.stopForValidationErrors = true;
                } else {
                    this.stopForValidationErrors = false;
                }
            }
        }
    }
}
